import React, { memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import HelpIcon from '@mui/icons-material/Help';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import {
  DeliveryBonusType,
  MenuItemsUpdateInfo,
} from '@provider-portal/common/api/gen/CampaignDeliveryProvider';
import { format } from 'date-fns';
import { SavedMenuItemSelector } from 'promotions/components/PromotionSummary/SavedMenuItemSelector';

import { useSelectedProvider } from 'root/hooks/useSelectedProvider';

import { Promotion, PromotionDuration } from '../../types';

export interface PromotionSummaryFCProps {
  promotion: Promotion;
  duration: PromotionDuration;
  strings: {
    promotionTitle: string;
    durationTitle: string;
    exampleTitle: string;
    exampleType: string;
    exampleBasePrice: string;
    exampleDiscountedPrice: string;
    exampleDescription: string;
    exampleTooltip: string;
    viewAll: string;
    appliesToItemsTitle: string;
  };
  selectedMenuItemNames: string;
  selectedProductIds: string[];
  exclusionTags: string[];
  footer?: ReactNode;
  hideMenuEditor: boolean;
  onMenuItemsUpdate: (newItemsToUpdate: MenuItemsUpdateInfo[]) => void;
}

const styles = {
  exampleBasePrice: {
    textDecoration: 'line-through',
  },
  helpIcon: {
    display: 'block',
  },
  bottom: {
    paddingBottom: 0,
  },
  viewAll: {
    padding: 0,
    justifyContent: 'left',
    textDecoration: 'underline',
    fontWeight: 'normal',
  },
};

const menuItemsTextLengthToClip = 250;

export const PromotionSummaryFC = memo(
  ({
    promotion,
    duration,
    footer,
    strings,
    selectedMenuItemNames,
    selectedProductIds,
    exclusionTags,
    hideMenuEditor,
    onMenuItemsUpdate,
  }: PromotionSummaryFCProps) => {
    const [shouldShowTooltip, setShowTooltip] = useState(false);
    const [shouldShowMenuSelector, setShouldShowMenuSelector] = useState(false);
    const toggleMenuSelectorWindow = useCallback(() => {
      setShouldShowMenuSelector(!shouldShowMenuSelector);
    }, [shouldShowMenuSelector]);

    const [shouldShowAllMenuItems, setShouldShowAllMenuItems] = useState(
      selectedMenuItemNames.length <= menuItemsTextLengthToClip,
    );

    const { showTooltip, hideTooltip } = useMemo(
      () => ({
        showTooltip: () => setShowTooltip(() => true),
        hideTooltip: () => setShowTooltip(() => false),
      }),
      [],
    );

    const shouldShowMenuSelectorDialog = useMemo(() => {
      return shouldShowMenuSelector;
    }, [shouldShowMenuSelector]);

    const shouldShowMenuItems = useMemo(() => {
      return selectedMenuItemNames.length > 0;
    }, [selectedMenuItemNames]);

    useEffect(() => {
      setShouldShowAllMenuItems(selectedMenuItemNames.length <= menuItemsTextLengthToClip);
    }, [selectedMenuItemNames.length]);

    const selectedMenuItemNamesToShow = useMemo(() => {
      return shouldShowAllMenuItems
        ? selectedMenuItemNames
        : selectedMenuItemNames.substring(0, menuItemsTextLengthToClip);
    }, [selectedMenuItemNames, shouldShowAllMenuItems]);

    const showAllItems = useCallback(() => {
      setShouldShowAllMenuItems(true);
    }, [setShouldShowAllMenuItems]);

    const durationString = useMemo(() => {
      const startString = format(duration.start, 'MMM dd, yyyy');
      const endString = format(duration.end, 'MMM dd, yyyy');
      return `${startString} - ${endString}`;
    }, [duration.start, duration.end]);

    const currentProvider = useSelectedProvider();

    const isPromotionDescriptionVisible = useMemo(() => {
      // Promotion example does not make sense in case of delivery discount
      const isBonusForDelivery =
        promotion.campaignBonusType === DeliveryBonusType.PERCENTAGE_OF_BASE_DELIVERY_PRICE ||
        promotion.campaignBonusType === DeliveryBonusType.PERCENTAGE_OF_FULL_DELIVERY_PRICE;

      return (
        currentProvider.countryCode.toLowerCase() !== 'mt' &&
        currentProvider.countryCode.toLowerCase() !== 'lt' &&
        (!promotion.campaignBonusType || !isBonusForDelivery)
      );
    }, [promotion, currentProvider]);

    return (
      <Grid container spacing="32px">
        <Grid item xs={5}>
          <Typography variant="bodyM">{strings.promotionTitle}</Typography>
        </Grid>
        <Grid item xs={5}>
          <Box>
            <Typography variant="bodyM" color="textPrimary">
              {promotion.title}
            </Typography>
          </Box>
          <Box>
            <Typography variant="bodyM">{promotion.description}</Typography>
          </Box>
        </Grid>
        <Grid item xs={2} />
        {shouldShowMenuItems && (
          <>
            <Grid item xs={5}>
              <Typography variant="bodyM">{strings.appliesToItemsTitle}</Typography>
            </Grid>
            <Grid item xs={5}>
              <Box>
                <Typography variant="bodyM" color="textPrimary">
                  {selectedMenuItemNamesToShow}
                </Typography>
              </Box>
              {!shouldShowAllMenuItems && (
                <Button variant="text" onClick={showAllItems} sx={styles.viewAll}>
                  {strings.viewAll}
                </Button>
              )}
            </Grid>
            <Grid item xs={2}>
              {!hideMenuEditor && (
                <Button variant="text" onClick={toggleMenuSelectorWindow} sx={styles.viewAll}>
                  <EditIcon />
                </Button>
              )}

              {shouldShowMenuSelectorDialog && (
                <SavedMenuItemSelector
                  exclusionTags={exclusionTags}
                  savedMenuItemProductIds={selectedProductIds}
                  onItemListToggle={toggleMenuSelectorWindow}
                  onMenuItemsUpdate={onMenuItemsUpdate}
                />
              )}
            </Grid>
          </>
        )}
        <Grid item xs={5}>
          <Typography variant="bodyM">{strings.durationTitle}</Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography variant="bodyM" color="textPrimary">
            {durationString}
          </Typography>
        </Grid>
        <Grid item xs={2} />
        {isPromotionDescriptionVisible && (
          <>
            <Grid item xs={5}>
              <Typography variant="bodyM">{strings.exampleTitle}</Typography>
            </Grid>
            <Grid item xs={5}>
              <Box display="flex" flexDirection="column">
                <Typography color="textPrimary" variant="bodyM">
                  {strings.exampleType}
                </Typography>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Box>
                    <Typography color="textPrimary" variant="bodyM">
                      {strings.exampleDiscountedPrice}
                    </Typography>
                  </Box>
                  <Box ml={1}>
                    <Typography variant="bodyM" sx={styles.exampleBasePrice}>
                      {strings.exampleBasePrice}
                    </Typography>
                  </Box>
                  <Box ml={1}>
                    <Tooltip
                      arrow
                      title={strings.exampleTooltip}
                      enterDelay={100}
                      onOpen={showTooltip}
                      onClose={hideTooltip}
                      placement="top-end"
                    >
                      <Box mt={0.5}>
                        <Typography variant="bodyM">
                          {shouldShowTooltip ? (
                            <HelpIcon color="secondary" sx={styles.helpIcon} fontSize="small" />
                          ) : (
                            <HelpOutlineIcon
                              color="inherit"
                              sx={styles.helpIcon}
                              fontSize="small"
                            />
                          )}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography variant="bodyS">{strings.exampleDescription}</Typography>
              </Box>
            </Grid>
            <Grid item xs={2} />
          </>
        )}
        {footer && (
          <Grid item xs={12}>
            {footer}
          </Grid>
        )}
      </Grid>
    );
  },
);
